/**
 * Transforms the curriculum service response into the shape that
 * downstream components expect from the legacy route-service +
 * content-service pipeline.
 *
 * Legacy shape contract:
 *   routeContent.route  → { entryId, queryType, app, ...rest }
 *   routeContent.content → { [queryType]: { displayTitle, ...pageFields } }
 *
 * Curriculum service shape (normalized ContentPage):
 *   route → { pageExternalId, routeElementExternalId, pageType, ...rest }
 *   page  → { page: { id, type, title, sections[], metadata{} }, ...audienceFields }
 *
 * The normalizer in the curriculum service restructures Contentful entries:
 *   - displayTitle → title
 *   - Scalar fields (description, programs, etc.) → metadata
 *   - Section arrays (programSubsections, content, etc.) → sections[]
 *
 * This transform reconstructs the original Contentful field names so that
 * existing React components continue to work unchanged.
 */

/**
 * Maps content type names to their original Contentful section-array field
 * name(s). When the normalizer flattens Collection fields into `sections[]`,
 * this map lets us put them back under the key the component expects.
 */
const SECTIONS_FIELD_MAP = {
  programSectionArticleTree: 'programSubsections',
  subsectionLanding: 'content',
  pageGeneralInfo: 'sections',
  article: 'content',
  microlearning: 'content',
  extensionLanding: 'content',
  unitHs: 'weeks',
  modalFlow: 'modals',
  navigationPage: 'sections',
  presentation: 'sections',
}

/**
 * Maps the curriculum route object to the legacy route shape.
 *
 * The queryType (content type) comes from the curriculum route's pageType,
 * the resolved page segment's pageType, or — as a last resort — the
 * inner Contentful page blob's `type` (the blob is passed through opaque,
 * so its key casing is not guaranteed by the response schema).
 *
 * The curriculum service top-level route + page segments are camelCase per
 * `serve-audienced-content-response-schema.ts`; legacy snake_case fallbacks
 * have been removed.
 */
export function transformRoute({ curriculumRoute, pageResult, app }) {
  if (!curriculumRoute) return null

  const entryId =
    curriculumRoute.pageExternalId || curriculumRoute.routeElementExternalId

  const queryType =
    curriculumRoute.pageType ||
    pageResult?.pageType ||
    pageResult?.page?.type ||
    'navigationPage'

  return {
    ...curriculumRoute,
    entryId,
    queryType,
    app,
  }
}

/**
 * Converts a snake_case string to camelCase.
 */
function snakeToCamel(str) {
  return str.replace(/_([a-z0-9])/g, (_, ch) => ch.toUpperCase())
}

/**
 * Recursively converts all snake_case object keys to camelCase.
 * Leaves arrays, primitives, and null unchanged.
 */
function camelizeKeys(obj) {
  if (obj == null || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(camelizeKeys)

  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    result[snakeToCamel(key)] = camelizeKeys(value)
  }
  return result
}

/**
 * Section types that are linked single entries (not child content items).
 * The normalizer incorrectly includes these in sections[] because they
 * are objects with sys/contentType. They belong in metadata instead.
 */
const NON_CONTENT_SECTION_TYPES = new Set(['Program'])

/**
 * Detects whether an object is a normalized ContentPage from the curriculum
 * service (has `sections` array and `metadata` object).
 */
function isNormalizedContentPage(obj) {
  return (
    obj != null &&
    typeof obj === 'object' &&
    Array.isArray(obj.sections) &&
    obj.metadata != null &&
    typeof obj.metadata === 'object'
  )
}

/**
 * Reconstructs the legacy Contentful field structure from a normalized
 * ContentPage so that downstream components can destructure the same
 * property names they always have (displayTitle, description, etc.).
 *
 * Applies two transformations:
 * 1. snake_case → camelCase key conversion (the curriculum service
 *    normalizer stores Contentful fields in snake_case)
 * 2. Filters out non-content sections (e.g. linked Program entries)
 *    that the normalizer incorrectly placed in sections[]
 */
function denormalizePage(page, queryType) {
  const camelMetadata = camelizeKeys(page.metadata)
  const result = {
    ...camelMetadata,
    displayTitle: page.title,
    id: page.id,
  }

  const contentSections = page.sections.filter(
    s => !NON_CONTENT_SECTION_TYPES.has(s.type),
  )

  const sectionField = SECTIONS_FIELD_MAP[queryType] || 'sections'
  result[sectionField] = contentSections.map(camelizeKeys)

  return result
}

/**
 * Maps the curriculum page payload to the legacy content shape.
 * The old content-service returned content keyed by queryType, e.g.:
 *   { navigationPage: { displayTitle: "My Page", ... } }
 *
 * When the page is a normalized ContentPage from the curriculum service,
 * the original Contentful field names are reconstructed so that existing
 * components work without modification.
 */
export function transformContent({ pageResult, queryType }) {
  if (!pageResult) return {}

  const pageData = pageResult?.page || pageResult

  if (isNormalizedContentPage(pageData)) {
    return {
      [queryType]: denormalizePage(pageData, queryType),
    }
  }

  return {
    [queryType]: pageData,
  }
}

/**
 * Combines route and content transforms into a single routeContent object
 * matching the legacy shape consumed by PagesV2, FamilyResources, etc.
 */
export function transformCurriculumResponse({
  curriculumRoute,
  pageResult,
  app,
  loading,
  error,
  changeLocale,
  locale,
  coursePointer,
  contentGroupCode,
}) {
  const route = transformRoute({ curriculumRoute, pageResult, app })
  const queryType = route?.queryType || 'navigationPage'
  const content = transformContent({ pageResult, queryType })

  return {
    route,
    content,
    loading: loading || (!error && !route),
    error,
    changeLocale,
    locale,
    coursePointer,
    contentGroupCode,
  }
}
